import { createQuery } from 'react-query-kit';

import * as Types from '../api/reports/types.api';
import * as API from '../api/reports/index.api';
import * as footprintAPI from '../api/footprint/index.api';
import { getCountryName } from 'src/utils/countryData';

//
//

const key = 'reports';

//

export const useQueryImpactStatsGet = createQuery<
  Types.ImpactStatsResponse,
  Types.ImpactStatsQueryParams
>({
  queryKey: [key, 'impact-range'],
  fetcher: (params) => API.getImpactStatsAPI(params),
});

//

export const useQueryCumulativeImpactStatsGet = createQuery<
  Types.ImpactStatsResponse,
  Types.ImpactStatsQueryParams
>({
  queryKey: [key, 'impact-cumulative'],
  fetcher: (params) => API.getImpactStatsAPI(params),
});

//

export const useQueryReportsPurchasesByMerchantList = createQuery<
  Types.PurchasesByMerchantResponse,
  Types.PurchasesByMerchantQueryParams
>({
  queryKey: [key, 'purchases-by-merchant'],
  fetcher: (params) => API.getPurchasesByMerchantCodeAPI(params),
});

//

export const useQueryReportsJourneyStatsList = createQuery<
  Types.JourneyStatsResponse,
  Types.GenericStatsQueryParams
>({
  queryKey: [key, 'journey-stats'],
  fetcher: (params) => API.getJourneyStatsAPI(params),
});

//

export const useQueryReportsAviationStatsList = createQuery<
  Types.AviationStatsResponse,
  Types.GenericStatsQueryParams
>({
  queryKey: [key, 'aviation-stats'],
  fetcher: (params) => API.getAviationStatsAPI(params),
});

//

export const useQueryReportsEmployeesStatsList = createQuery<
  Types.MerchantEmployeeResponse,
  Types.GetMerchantEmployeeQueryParams
>({
  queryKey: [key, 'employees-stats'],
  fetcher: (params) => API.getMerchantEmployeesAPI(params),
});

//

export const useQueryReportsMerchantEmployeesEnriched = createQuery<
  Types.MerchantEmployeeResponse,
  Types.GetMerchantEmployeeQueryParams
>({
  queryKey: [key, 'employees-enriched'],
  fetcher: async (params) => {
    const baseData = await API.getMerchantEmployeesAPI(params);

    const userIds = baseData.content.map((r: any) => r.carbonUserId);
    if (!userIds?.length) return baseData;

    const [footprintsResponse, actionsResponse, commuteResponse] = await Promise.all([
      footprintAPI.getUserFootprintByUserIdsAPI(userIds),
      footprintAPI.getUserActionCountByUserIdsAPI(userIds),
      footprintAPI.getUserCommuteFootprintByUserIdsAPI(userIds),
    ]);

    const footprintsById = footprintsResponse.content.reduce(
      (a: any, f) => ({ ...a, [f.carbonUserId]: f }),
      {}
    );
    const actionCountById = actionsResponse.content.reduce(
      (a: any, r) => ({ ...a, [r.carbonUserId]: r.engagementCount }),
      {}
    );
    const commuteByCarbonUserId = commuteResponse.content.reduce(
      (a: any, r) => ({ ...a, [r.carbonUserId]: r.totalCommuteFootprintKg }),
      {}
    );

    const enrichedContent = baseData?.content.map((record) => {
      const footprint = footprintsById[record.carbonUserId];
      const userCountryCode = footprint?.answers?.userCountryCode;
      return {
        ...record,
        numberOfCommittedActions: actionCountById[record.carbonUserId] || null,
        totalPersonalFootprintKg: footprint?.totalPersonalFootprintKg,
        totalCommuteFootprintKg: commuteByCarbonUserId[record.carbonUserId],
        location: (userCountryCode && getCountryName(userCountryCode)) || '',
      };
    });

    return { ...baseData, content: enrichedContent };
  },
});

//

export const useQueryReportsEngagementSummaryGet = createQuery<
  Types.EngagementSummaryResponse,
  Types.EngagementSummaryQueryParams
>({
  queryKey: [key, 'engagement-summary'],
  fetcher: (params) => API.getEngagementSummaryAPI(params),
});

//

export const useQueryReportsEngagementDisctributionGet = createQuery<
  Types.EngagementDisctributionResponse,
  Types.EngagementSummaryQueryParams
>({
  queryKey: [key, 'engagement-distribution'],
  fetcher: (params) => API.getEngagementDistributionAPI(params),
});
